import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { sanitizePayloadDeep } from '@/utils/inputSanitizer';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error('VITE_SUPABASE_URL is not defined');
}

if (!supabaseKey) {
  throw new Error('VITE_SUPABASE_ANON_KEY is not defined');
}

const globalForSupabase = globalThis as typeof globalThis & {
  supabaseClient?: SupabaseClient;
};

const WRITE_METHODS = new Set(['insert', 'update', 'upsert']);

type QueryBuilderMethod = (...args: unknown[]) => unknown;

const getProperty = <T extends object>(
  target: T,
  prop: PropertyKey,
  receiver: unknown,
): unknown => Reflect.get(target, prop, receiver as object);

const wrapQueryBuilderWithSanitizer = <T extends object>(builder: T): T =>
  new Proxy(builder, {
    get(target, prop, receiver) {
      const original = getProperty(target, prop, receiver);

      if (typeof prop === 'string' && WRITE_METHODS.has(prop) && typeof original === 'function') {
        return (...args: unknown[]) => {
          const [payload, ...rest] = args;
          return (original as QueryBuilderMethod).call(
            target,
            sanitizePayloadDeep(payload),
            ...rest,
          );
        };
      }

      return original;
    },
  });

const createSanitizedSupabaseClient = (): SupabaseClient => {
  const client = createClient(supabaseUrl, supabaseKey, {
    auth: {
      detectSessionInUrl: false,
    },
  });

  return new Proxy(client, {
    get(target, prop, receiver) {
      const original = getProperty(target, prop, receiver);

      if (prop === 'from' && typeof original === 'function') {
        return (...args: unknown[]) => {
          const builder = (original as QueryBuilderMethod).apply(target, args);
          if (builder && typeof builder === 'object') {
            return wrapQueryBuilderWithSanitizer(builder as object);
          }
          return builder;
        };
      }

      return original;
    },
  });
};

export const supabase = globalForSupabase.supabaseClient ?? createSanitizedSupabaseClient();

if (import.meta.env.DEV) {
  globalForSupabase.supabaseClient = supabase;
}

export const createIsolatedSupabaseClient = (): SupabaseClient => {
  const client = createClient(supabaseUrl, supabaseKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  });

  return new Proxy(client, {
    get(target, prop, receiver) {
      const original = getProperty(target, prop, receiver);

      if (prop === 'from' && typeof original === 'function') {
        return (...args: unknown[]) => {
          const builder = (original as QueryBuilderMethod).apply(target, args);
          if (builder && typeof builder === 'object') {
            return wrapQueryBuilderWithSanitizer(builder as object);
          }
          return builder;
        };
      }

      return original;
    },
  });
};

export const SUPABASE_URL = supabaseUrl;
export const SUPABASE_ANON_KEY = supabaseKey;
